import {
  Card,
  CardContent,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  CreditCard,
  Filter,
  RefreshCw,
  Search,
  XCircle
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import adminApi from '../../api/admin';
import Button from '../../components/common/Button';
import DataTable from '../../components/common/DataTable';
import useFetchTableData from '../../hooks/useFetchTableData';
import { formatDateForDisplay } from '../../utils/helpers';

const AdminPaymentsPage = () => {
  const [filters, setFilters] = useState({
    status: "all",
    search: "",
    gateway: "all",
  });

  const {
    data,
    isLoading,
    isError,
    error,
    paginationModel,
    setPaginationModel,
  } = useFetchTableData({
    queryKey: "payments",
    fetchFunc: adminApi.getPayments,
    params: filters,
  });

  // Define columns for the DataGrid
  const columns = [
    // { 
    //   field: 'id', 
    //   headerName: 'ID', 
    //   width: 80,
    //   align: 'center',
    //   headerAlign: 'center'
    // },
    { 
      field: 'transaction_id', 
      headerName: 'Transaction ID', 
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <div className="font-mono text-sm flex items-center">
          <Link to={`${params.row.id}`} className="text-blue-600 hover:underline font-semibold">{params.value}</Link>
        </div>
      )
    },
    { 
      field: 'amount', 
      headerName: 'Amount', 
      width: 120,
      renderCell: (params) => (
        <div className="flex items-center space-x-1">
          {/* <DollarSign className="w-4 h-4 text-green-600" /> */}
          <span className="font-bold">{params.value}</span>
          <span className="text-xs text-gray-500 ml-1">
            {params.row.currency}
          </span>
        </div>
      )
    },
    { 
      field: 'gateway', 
      headerName: 'Gateway', 
      width: 120,
      renderCell: (params) => {
        const gatewayConfig = {
          stripe: { color: 'primary', label: 'Stripe' },
          daraja: { color: 'secondary', label: 'Daraja' },
          paypal: { color: 'info', label: 'PayPal' }
        };
        
        const config = gatewayConfig[params.value] || { color: 'default', label: params.value };
        
        return (
          <Chip 
            label={config.label}
            color={config.color}
            size="small"
            variant="outlined"
          />
        );
      }
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 130,
      renderCell: (params) => {
        const statusConfig = {
          completed: { color: 'success', label: 'Completed', icon: CheckCircle },
          pending: { color: 'warning', label: 'Pending', icon: Clock },
          failed: { color: 'error', label: 'Failed', icon: XCircle },
          refunded: { color: 'info', label: 'Refunded', icon: RefreshCw },
          canceled: { color: 'default', label: 'Canceled', icon: XCircle }
        };
        
        const config = statusConfig[params.value] || { color: 'default', label: params.value, icon: AlertCircle };
        
        return (
          <Chip 
            label={config.label}
            color={config.color}
            size="small"
            icon={<config.icon className="w-4 h-4" />}
          />
        );
      }
    },
    { 
      field: 'subscription', 
      headerName: 'Subscription ID', 
      width: 140,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <div className="text-center">
          <div className="font-semibold">{params.value}</div>
        </div>
      )
    },
    { 
      field: 'created_at', 
      headerName: 'Created', 
      width: 120,
      align: 'center',
      renderCell: (params) => (
        <div className="text-sm">
          {formatDateForDisplay(params.value)}
        </div>
      )
    },
    { 
      field: 'updated_at', 
      headerName: 'Updated', 
      width: 120,
      renderCell: (params) => (
        <div className="text-sm">
          {formatDateForDisplay(params.value)}
        </div>
      )
    },
    // {
    //   field: 'actions',
    //   type: 'actions',
    //   headerName: 'Actions',
    //   width: 100,
    //   getActions: (params) => [
    //     <GridActionsCellItem
    //       icon={<Eye className="w-4 h-4" />}
    //       label="View Details"
    //       onClick={() => handleView(params.row)}
    //       showInMenu
    //     />,
    //     <GridActionsCellItem
    //       icon={<RefreshCw className="w-4 h-4" />}
    //       label="Refresh Status"
    //       onClick={() => handleRefresh(params.row)}
    //       showInMenu
    //     />,
    //   ],
    // },
  ];

  // Handler functions
  const handleView = (payment) => {
    console.log('View payment:', payment);
    // Navigate to payment details page
    // navigate(`/admin/payments/${payment.id}`);
  };

  const handleRefresh = (payment) => {
    console.log('Refresh payment:', payment);
    // Implement refresh logic
  };

  const handleStatusFilterChange = (event) => {
    setFilters(prev => ({
      ...prev,
      status: event.target.value
    }));
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleGatewayFilterChange = (event) => {
    setFilters(prev => ({
      ...prev,
      gateway: event.target.value
    }));
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleSearchChange = (event) => {
    setFilters(prev => ({
      ...prev,
      search: event.target.value
    }));
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: "all",
      search: "",
      gateway: "all",
    });
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // Check if any filters are active
  const hasActiveFilters = filters.status !== 'all' || filters.search || filters.gateway !== 'all';


  if (isError) {
    return (
      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading payments: {error?.message}</p>
          <Button 
            onClick={() => window.location.reload()} 
            variant="outline" 
            className="mt-4"
            icon={<RefreshCw className="w-4 h-4 mr-2" />}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Payment Management</h1>
          <p className="text-secondary-600 mt-1">
            {data ? `Total: ${data.count} payments` : 'Monitor and manage payment transactions'}
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Button 
            onClick={handleClearFilters}
            variant="outline"
            disabled={!hasActiveFilters}
            icon={<RefreshCw className="w-4 h-4 mr-2" />}
          >
            Clear Filters
          </Button>
        </div>
      </div>
      

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <Filter className="w-5 h-5 text-secondary-500" />
              <Typography variant="subtitle1" className="text-secondary-900">
                Filters
              </Typography>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <TextField
                size="small"
                placeholder="Search transactions..."
                value={filters.search}
                onChange={handleSearchChange}
                slotProps={{input:{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search className="w-4 h-4 text-gray-400" />
                    </InputAdornment>
                  ),
                }}}
                className="w-full sm:w-64"
              />
              <FormControl size="small" className="min-w-[150px]">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={handleStatusFilterChange}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="refunded">Refunded</MenuItem>
                  {/* <MenuItem value="cancelled">Cancelled</MenuItem> */}
                </Select>
              </FormControl>
              <FormControl size="small" className="min-w-[150px]">
                <InputLabel>Gateway</InputLabel>
                <Select
                  value={filters.gateway}
                  label="Gateway"
                  onChange={handleGatewayFilterChange}
                >
                  <MenuItem value="all">All Gateways</MenuItem>
                  <MenuItem value="stripe">Stripe</MenuItem>
                  <MenuItem value="daraja">Daraja</MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* DataGrid */}
        <DataTable
          rows={data?.results || []}
          columns={columns}
          loading={isLoading}
          rowCount={data?.count || 0}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
       
          
        />

      {/* Empty State */}
      {!isLoading && data?.results?.length === 0 && (
        <Card className="mt-6">
          <CardContent className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-8 h-8 text-gray-400" />
            </div>
            <Typography variant="h6" className="text-secondary-900 mb-2">
              No payments found
            </Typography>
            <Typography variant="body2" className="text-secondary-600 mb-4">
              {hasActiveFilters 
                ? 'No payments match your current filters.'
                : 'There are no payment transactions in the system yet.'
              }
            </Typography>
            {hasActiveFilters && (
              <Button 
                onClick={handleClearFilters}
                variant="outline"
              >
                Clear Filters
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminPaymentsPage;